/* app/onboarding/steps.jsx — сценарий обязательного игрового онбординга («квест»).
   Декларативный список шагов; логику (навигация, завершение, конфетти) держит
   OnboardingOverlay. Тур ОБЯЗАТЕЛЬНЫЙ — кнопки «пропустить» нет.

   Виды шага:
   - kind:'panel'     — центрированная карточка (welcome/celebrate/business/finish).
   - kind:'spotlight' — подсветка реального элемента (target) + подсказка-пузырь.

   Условие перехода (advance) у spotlight-шага:
   - {type:'button', label}   — кнопка в подсказке;
   - {type:'event',  name}    — window CustomEvent (onb:sent / onb:committed);
   - {type:'appear', selector}— селектор появился в DOM (результат ИИ);
   - {type:'goto',   match}   — пользователь сам перешёл: URL (pathname+search)
                                содержит match. У таких шагов clickThrough:true —
                                затемнение НЕ блокирует клик, чтобы можно было нажать
                                подсвеченный пункт меню/вкладку.
   Кнопка подсказки cta:{label, to} у goto-шага — переход приложения по адресу to (плейсхолдеры
   из tourVars, stepCta); тур идёт дальше тем же goto. Нет значения плейсхолдера — нет кнопки.

   Режим «Продукции» (tourSteps.applyKitchenMode, kitchen-ux E1c): в режиме кассы вкладок
   «Полуфабрикаты» и «Готовые позиции» нет — несвязанные с кассой техкарты во вкладке «Не в кассе»
   (бывшие «Черновики», pos-base-rework; тот же id drafts, сегменты ?kind=, якоря
   data-onb="draft-…"), а legacy-редирект уводит tab=semiproducts|recipes в tab=drafts&kind=….
   Шаг «Продукции» несёт pos:{…} — поля, которые в режиме кассы другие (цель, переход, текст,
   кнопка). Режим объявляет оболочка «Продукции» (lib/kitchenModeSignal.js), пока не объявлен —
   classic. Проверка «цель и переход есть в обоих режимах» — stepsModes.test.js.

   Привязка к разделу тарифа (tourSteps.js, lib/planSections.js):
   - section:'analytics'       — шаг пропускается, если тариф пространства закрыл раздел
                                 (workspace.sections.analytics === false);
   - copyWithout:{analytics:{…}} — поля, которые подменяют текст шага при закрытом разделе. */

import { REPORTS_SECTION_ENABLED } from '../../lib/reportsSection.js';

// Курированный пример: блюдо с полуфабрикатом (тунец конфи) — нейросеть сама соберёт
// и ТТК блюда, и отдельный п/ф, и карточки сырья (демонстрация авто-сборки дерева).
export const DEMO_TTK_PROMPT =
  'Сделай ТТК на салат Нисуаз с тунцом конфи для ресторана формата casual dining. ' +
  'Выход салата 260гр.';

/* Подстановки в тексты и селекторы шагов из события onb:committed (DraftEntityCard):
   {dish} — название созданного блюда; {dishId} — id ТТК для строки реестра
   (data-focus-key="ttk-<id>"). Нет id ТТК (дубль названия, сущность другого типа) — пустая
   строка: OnboardingOverlay.interp тогда оставляет «{dishId}» в селекторе, он ни с чем не
   совпадает, и шаг берёт запасную цель. */
export function tourVars(committed) {
  const c = committed || {};
  return {
    dish: c.title || 'ваше блюдо',
    dishId: c.kind === 'ttk' && c.id != null && c.id !== '' ? String(c.id) : '',
  };
}

/* Кнопка подсказки шага (cta: {label, to}) — переход приложения, после которого тур идёт дальше
   по адресу (advance goto). Плейсхолдеры адреса — из vars, экранированные; хоть одного нет —
   кнопки нет (null): подсказка шага сама говорит, что делать. */
export function stepCta(step, vars) {
  const cta = step && step.cta;
  if (!cta || !cta.to) return null;
  const v = vars || {};
  let ready = true;
  const to = cta.to.replace(/\{(\w+)\}/g, (m, k) => {
    if (v[k] == null || v[k] === '') { ready = false; return m; }
    return encodeURIComponent(String(v[k]));
  });
  return ready ? { label: cta.label, to } : null;
}

// Бургер, открывающий навигацию на узких раскладках (сайдбар свёрнут):
// ChatHeader (на /chat) и PageHeader (в разделах).
const NAV_HAMBURGER = 'button[aria-label="Меню и диалоги"], .page-header__menu';

// №524: раздел «Отчёты» выключен к релизу 11.11 — его шагов в туре нет, тексты называют
// разделы без него (рубильник — lib/reportsSection.js).
const WITH_REPORTS = REPORTS_SECTION_ENABLED;
const REPORT_STEPS = [
  {
    id: 'go-reports', kind: 'spotlight', target: '[data-onb="nav-reports"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Отчёты»',
    title: 'Отчёты',
    body: 'Откройте меню (если свёрнуто) и нажмите «Отчёты» — последний раздел.',
    hint: 'Нажмите «Отчёты»',
    advance: { type: 'goto', match: '/reports' },
  },
  {
    id: 'reports', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Раздел «Отчёты»',
    body: 'Сюда попадают готовые AI-разборы: оптимизация фудкоста, ABC-анализ меню и инвентаризации — с выгрузкой в файл.',
    advance: { type: 'button', label: 'И последнее' },
  },
];

export const STEPS = [
  { id: 'welcome', kind: 'panel', panel: 'welcome' },

  {
    id: 'orient', kind: 'spotlight', target: '.sidebar__nav, .tab-bar',
    fallbackTarget: NAV_HAMBURGER, placement: 'auto',
    title: 'Ваше рабочее пространство',
    body: WITH_REPORTS
      ? 'Четыре раздела: Аргус, Продукция, Аналитика и Отчёты. Пройдём по ним — но сначала Аргус соберёт вашу первую техкарту.'
      : 'Три раздела: Аргус, Продукция и Аналитика. Пройдём по ним — но сначала Аргус соберёт вашу первую техкарту.',
    copyWithout: {
      analytics: { body: WITH_REPORTS
        ? 'Три раздела: Аргус, Продукция и Отчёты. Пройдём по ним — но сначала Аргус соберёт вашу первую техкарту.'
        : 'Два раздела: Аргус и Продукция. Пройдём по ним — но сначала Аргус соберёт вашу первую техкарту.' },
    },
    advance: { type: 'button', label: 'Дальше' },
  },
  {
    id: 'send', kind: 'spotlight', target: '.composer__send', fallbackTarget: '.composer',
    pad: 8, placement: 'top',
    collapseSidebar: true,   // фокус на чате: сворачиваем постоянный сайдбар на время сборки ТТК
    title: 'Нейросеть в деле',
    body: 'Мы уже вписали пример — техкарту салата. Нажмите кнопку «Отправить» (текст можно изменить) и смотрите, как Аргус её собирает.',
    hint: 'Нажмите «Отправить» →',
    advance: { type: 'event', name: 'onb:sent' },
  },
  {
    id: 'working', kind: 'spotlight', target: '.chat-conv__body', pad: 6, placement: 'left',
    collapseSidebar: true,
    title: 'Считает прямо сейчас',
    body: 'Нейросеть разбирает состав, рассчитывает КБЖУ, выход, себестоимость и фудкост. Несколько секунд.',
    hint: 'Нейросеть думает…',
    timed: true,            // показывать счётчик секунд в подсказке
    resendAfterMs: 30000,   // через 30с — «Долго отвечает? Отправить ещё раз» (10–15с — норма, раньше не пугаем)
    emergencyAfterMs: 60000, // через 60с (явный сбой LLM, после шанса на повтор) — мягкий «Продолжить»
    advance: { type: 'appear', selector: '[data-onb="draft-commit"]' },
  },
  {
    id: 'draft', kind: 'spotlight', target: '.msg--assistant:has([data-onb="draft-card"])',
    fallbackTarget: '[data-onb="draft-card"]', pad: 10, placement: 'left',
    collapseSidebar: true,
    title: 'Готовый черновик',
    body: 'Цифры посчитал код, а не выдумала модель. Любую граммовку можно поправить прямо здесь — пересчёт мгновенный.',
    advance: { type: 'button', label: 'Дальше' },
  },
  {
    id: 'commit', kind: 'spotlight', target: '[data-onb="draft-commit"]', pad: 8, placement: 'top',
    collapseSidebar: true,
    title: 'Сделайте её своей',
    body: 'Нажмите «Утвердить и добавить» — техкарта сохранится в разделе «Продукция».',
    hint: 'Нажмите «Утвердить и добавить»',
    advance: { type: 'event', name: 'onb:committed' },
  },

  { id: 'celebrate', kind: 'panel', panel: 'celebrate' },

  // ── Продукция (пользователь сам жмёт пункт меню) ──
  {
    id: 'go-kitchen', kind: 'spotlight', target: '[data-onb="nav-kitchen"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Продукция»',
    title: 'Где живёт техкарта',
    body: 'Откройте меню (если свёрнуто) и нажмите «Продукция» — посмотрим, куда попала ваша ТТК.',
    hint: 'Нажмите «Продукция»',
    advance: { type: 'goto', match: '/kitchen' },
  },
  {
    id: 'kitchen-sirye', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Сырьё завелось само',
    body: 'Это раздел «Продукция» — ядро системы: Сырьё, Полуфабрикаты, Готовые позиции и Проекты меню. Нейросеть уже завела сюда карточки сырья с КБЖУ и категориями — вручную ничего вводить не нужно.',
    advance: { type: 'button', label: 'А полуфабрикаты?' },
    // касса: вкладки другие (База POS, Не в кассе, Проекты меню; pos-base-rework) — сырьё
    // нейросети во вкладке «Не в кассе» (всё, что ещё не связано с кассой)
    pos: {
      body: 'Это раздел «Продукция» — ядро системы: база позиций кассы, техкарты «Не в кассе» и проекты меню. Нейросеть уже завела во вкладку «Не в кассе» карточки сырья с КБЖУ и категориями — вручную ничего вводить не нужно.',
    },
  },
  {
    id: 'kitchen-pf', kind: 'spotlight', target: '[data-onb="tab-semiproducts"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'И полуфабрикаты — тоже',
    body: 'Откройте «Полуфабрикаты»: сложную заготовку (тунец конфи) нейросеть собрала отдельным п/ф — со своим составом и себестоимостью. Все п/ф и ингредиенты создались автоматически.',
    hint: 'Нажмите «Полуфабрикаты»',
    advance: { type: 'goto', match: 'tab=semiproducts' },
    // касса: п/ф — сегмент «Не в кассе»; вкладка не открыта — сначала она (запасная цель)
    pos: {
      target: '[data-onb="draft-semiproducts"]', fallbackTarget: '[data-onb="tab-drafts"]',
      fallbackHint: 'Откройте «Не в кассе»',
      body: 'Откройте «Не в кассе» → «Полуфабрикаты»: сложную заготовку (тунец конфи) нейросеть собрала отдельным п/ф — со своим составом и себестоимостью. Все п/ф и ингредиенты создались автоматически.',
      advance: { type: 'goto', match: 'kind=semiproducts' },
    },
  },
  {
    id: 'kitchen-menu', kind: 'spotlight', target: '[data-onb="tab-menus"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'А ещё — меню',
    body: 'Из готовых блюд во вкладке «Проекты меню» собирается меню заведения: наценка, фудкост, печать. Создадите своё, когда будете готовы.',
    advance: { type: 'button', label: 'Где моё блюдо?' },
  },
  {
    id: 'kitchen-blyuda', kind: 'spotlight', target: '[data-onb="tab-recipes"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'Ваши готовые позиции',
    body: 'Откройте вкладку «Готовые позиции» — там лежит готовая техкарта.',
    hint: 'Нажмите «Готовые позиции»',
    advance: { type: 'goto', match: 'tab=recipes' },
    // касса: готовые блюда — сегмент «Блюда» вкладки «Не в кассе»
    pos: {
      target: '[data-onb="draft-recipes"]', fallbackTarget: '[data-onb="tab-drafts"]',
      fallbackHint: 'Откройте «Не в кассе»',
      body: 'Готовая техкарта — во вкладке «Не в кассе», в сегменте «Блюда».',
      hint: 'Нажмите «Блюда»',
      advance: { type: 'goto', match: 'kind=recipes' },
    },
  },
  {
    // Строка блюда в реестре «Готовые позиции» — по УСТОЙЧИВОМУ атрибуту слоя таблиц
    // (data-focus-key="ttk-<id>": и строка стола на 1740, и карточка на 380 — один <tr>), а не
    // по классу оформления: плитки .tile--recipe исчезли с волны C, и шаг терял цель (E1b).
    // Строку ищем ВНУТРИ реестра ТТК: тот же data-focus-key несут и ссылки «Где используется»
    // в карточке сырья. {dishId} — id ТТК из события onb:committed; его нет (дубль названия) —
    // запасная цель, весь реестр ТТК (data-registry="ttk").
    // Открыть строку щелчком нельзя: на столе щелчок по названию правит его в ячейке, стрелка
    // «Открыть полностью» видна при наведении, на 380 тап открывает «Быструю правку» под
    // затемнением тура. Поэтому у шага кнопка (cta): редактор этого блюда; тур идёт дальше по
    // адресу (edit=ttk) — так же, если человек откроет блюдо сам.
    id: 'kitchen-ttk', kind: 'spotlight', target: '[data-registry="ttk"] [data-focus-key="ttk-{dishId}"]', pad: 8, placement: 'auto',
    clickThrough: true, fallbackTarget: '[data-registry="ttk"]',
    fallbackHint: 'Найдите «{dish}» в списке и нажмите «Открыть полностью»',
    title: 'А вот она!',
    body: '«{dish}» — техкарта, которую собрала нейросеть: состав, себестоимость и фудкост уже посчитаны. Откройте её — посмотрим, что внутри.',
    hint: 'Откройте техкарту блюда',
    cta: { label: 'Открыть техкарту', to: '/kitchen?tab=recipes&edit=ttk-{dishId}' },
    advance: { type: 'goto', match: 'edit=ttk' },
    // касса: блюдо — «Не в кассе» → «Блюда» (tab=drafts); адрес сразу их (без legacy-редиректа и мелькания)
    pos: {
      cta: { label: 'Открыть техкарту', to: '/kitchen?tab=drafts&kind=recipes&edit=ttk-{dishId}' },
    },
  },
  {
    id: 'kitchen-edit', kind: 'spotlight', target: '.drawer[role="dialog"]', pad: 0, placement: 'left',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'Всё можно корректировать',
    body: 'Любую граммовку, состав, технологию или цену можно поменять прямо тут — пересчёт мгновенный. Закройте карточку, чтобы продолжить.',
    hint: 'Закройте карточку',
    advance: { type: 'goto', match: 'edit=ttk', gone: true },
  },

  // ── Аналитика ──
  {
    id: 'go-analytics', section: 'analytics', kind: 'spotlight', target: '[data-onb="nav-analytics"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Аналитика»',
    title: 'Аналитика',
    body: 'Откройте меню (если свёрнуто) и нажмите «Аналитика» — покажу сводку по вашей продукции.',
    hint: 'Нажмите «Аналитика»',
    advance: { type: 'goto', match: '/analytics' },
  },
  {
    id: 'analytics', section: 'analytics', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Раздел «Аналитика»',
    body: 'Дашборд по деньгам: самые дорогие блюда, тренды закупочных цен и всплески — чтобы держать себестоимость под контролем.',
    advance: { type: 'button', label: 'Дальше' },
  },

  // ── Отчёты (пока раздел выключен — шагов нет, №524) ──
  ...(WITH_REPORTS ? REPORT_STEPS : []),

  // ── Финал: бизнес-пространство + поздравление ──
  { id: 'business', kind: 'panel', panel: 'business' },
  {
    id: 'finish', kind: 'panel', panel: 'finish',
    // Текст финальной карточки живёт здесь (раньше был зашит в OnboardingOverlay), чтобы
    // его можно было подменить для пространства без аналитики.
    lead: WITH_REPORTS
      ? 'Собрали техкарту руками нейросети и прошли по Продукции, Аналитике и Отчётам. Дальше — за вами.'
      : 'Собрали техкарту руками нейросети и прошли по Продукции и Аналитике. Дальше — за вами.',
    copyWithout: {
      analytics: { lead: WITH_REPORTS
        ? 'Собрали техкарту руками нейросети и прошли по Продукции и Отчётам. Дальше — за вами.'
        : 'Собрали техкарту руками нейросети и прошли по Продукции. Дальше — за вами.' },
    },
  },
];
